/**
 * Input masks for the two numbers this business types constantly: a Jamaican TRN
 * and a phone number.
 *
 * Written as one core because the earlier pair of directives had the same three
 * faults each. They rewrote `event.target.value` inside an `input` listener added
 * in `mounted` - which runs after the listener v-model installs - so the model
 * kept the raw digits while the field showed the formatted ones, and the next
 * re-render put the raw digits back on screen. Nothing added the +1, and the
 * caret jumped to the end on every keystroke.
 *
 * Both formatters are idempotent, and the mask leaves a normalised number alone -
 * that is what makes it safe to re-dispatch the input event, since the second
 * pass finds nothing to change and stops.
 */

/** Digits only, capped at `max`. */
const digits = (value, max) => String(value ?? '').replace(/\D/g, '').slice(0, max)

/**
 * A TRN as ###-###-###, formatting what has been typed so far.
 * @param {unknown} value - Whatever is in the field.
 * @returns {string} The masked value.
 */
export const formatTrn = (value) => {
  const d = digits(value, 9)
  return d ? d.match(/.{1,3}/g).join('-') : ''
}

/**
 * A phone number as (###) ###-####, formatting what has been typed so far.
 *
 * A +1 already on the front is kept rather than swept into the digits. Without
 * that, re-masking a finished number - which happens when the field is left, and
 * again whenever a stored number is loaded into a form - read the 1 as the first
 * digit of the area code and turned +1 (876) 555-0111 into (187) 655-5011.
 *
 * @param {unknown} value - Whatever is in the field.
 * @returns {string} The masked value.
 */
export const formatPhone = (value) => {
  const raw = String(value ?? '').trim()
  // A number dialled to another country is not this mask's business.
  if (raw.startsWith('+') && !raw.startsWith('+1')) return raw

  const prefixed = raw.startsWith('+1')
  const d = digits(prefixed ? raw.slice(2) : raw, 10)
  const prefix = prefixed ? '+1 ' : ''

  if (d.length <= 3) return prefix + d
  if (d.length <= 6) return `${prefix}(${d.slice(0, 3)}) ${d.slice(3)}`
  return `${prefix}(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/**
 * The finished phone number, once the field is left alone.
 *
 * Jamaica and the United States are both +1, so a complete ten-digit number gets
 * it. A number already carrying 1 or +1 is not given a second one, and anything
 * dialled to another country is left exactly as it was typed rather than being
 * forced into a country code it does not belong to. An incomplete number is left
 * masked but unprefixed - claiming +1 over four digits would be a guess.
 *
 * @param {unknown} value - Whatever is in the field.
 * @returns {string} The number to store.
 */
export const normalizePhone = (value) => {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  if (raw.startsWith('+') && !raw.startsWith('+1')) return raw

  let d = raw.replace(/\D/g, '')
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1)

  return d.length === 10 ? `+1 ${formatPhone(d)}` : formatPhone(d)
}

/**
 * Build a directive that masks as you type and tidies up on the way out.
 * @param {(value: unknown) => string} format - Applied on every keystroke.
 * @param {(value: unknown) => string} [normalize] - Applied on blur.
 * @returns {Object} A Vue directive.
 */
export const mask = (format, normalize = format) => ({
  mounted(el) {
    const write = (next) => {
      if (next === el.value) return
      const atEnd = el.selectionStart === el.value.length
      el.value = next
      // v-model reads this event, so it has to see the masked value or the two
      // drift apart. Idempotent formatters make the re-entry a no-op.
      el.dispatchEvent(new Event('input', { bubbles: true }))
      if (atEnd && el.setSelectionRange) el.setSelectionRange(next.length, next.length)
    }

    el._mask = {
      input: () => write(format(el.value)),
      blur: () => write(normalize(el.value))
    }
    el.addEventListener('input', el._mask.input)
    el.addEventListener('blur', el._mask.blur)

    // A field the form has already filled in - editing a client, say.
    if (el.value) write(format(el.value))
  },
  beforeUnmount(el) {
    if (!el._mask) return
    el.removeEventListener('input', el._mask.input)
    el.removeEventListener('blur', el._mask.blur)
    delete el._mask
  }
})
